import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { CacheAwareRefreshButton } from '../../../components/DataTable/CacheRefreshButton';
import { ColumnFiltersToggle } from '../../../components/DataTable/Toolbar';
import { ExportCsv, ExportProps } from '../../../components/ExportCsv';
import { ColumnChanger, ColumnChangerProps } from '../../../design-system';
import { selectedChargePoints, TableChargePoint } from './utils';

type LabelledProps = { label: string; children: React.ReactNode };

const Labelled: React.FC<LabelledProps> = ({ label, children }) => (
  <Box display="flex" flexDirection="column" alignItems="center">
    {children}
    <Typography variant="caption" color="textSecondary">
      {label}
    </Typography>
  </Box>
);

type ApplyUpdateActionProps = {
  chargePoints: TableChargePoint[];
  count: number;
  onFlexUpgrade: () => void;
};

const ApplyUpdateAction: React.FC<ApplyUpdateActionProps> = ({ chargePoints, count, onFlexUpgrade }) => {
  const { push } = useHistory();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleSendCommand = () => {
    setAnchorEl(null);
    push(`/admin/charge-points-command`, { chargePoints: selectedChargePoints(chargePoints) });
  };

  const handleFlexUpgrade = () => {
    setAnchorEl(null);
    onFlexUpgrade();
  };

  return (
    <>
      <Button
        color="primary"
        disabled={count === 0}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        variant="contained"
        endIcon={<ArrowDropDownIcon />}
      >
        {count > 0 ? `Apply update (${count})` : 'Apply update'}
      </Button>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={handleSendCommand}>Send OCPP command</MenuItem>
        <MenuItem onClick={handleFlexUpgrade}>Flex Upgrade / Downgrade</MenuItem>
      </Menu>
    </>
  );
};

type Props = {
  tableProps: ColumnChangerProps & ExportProps<TableChargePoint>;
  filtering: boolean;
  onFilteringChange: React.Dispatch<React.SetStateAction<boolean>>;
  onRefresh: () => void;
  isLoading: boolean;
  cacheInfo: { lastUpdated?: string } | undefined;
  allChargePoints: TableChargePoint[];
  selectedCount: number;
  onFlexUpgrade: () => void;
};

export const ChargePointsToolbar: React.FC<Props> = ({
  tableProps,
  filtering,
  onFilteringChange,
  onRefresh,
  isLoading,
  cacheInfo,
  allChargePoints,
  selectedCount,
  onFlexUpgrade,
}) => (
  <Box display="flex" alignItems="center" gap={1}>
    <Labelled label="Columns">
      <ColumnChanger
        columnsButton={tableProps.columnsButton}
        columns={tableProps.columns}
        icons={tableProps.icons}
        onColumnsChanged={tableProps.onColumnsChanged}
      />
    </Labelled>
    <Labelled label="Filters">
      <ColumnFiltersToggle filtering={filtering} setFiltering={onFilteringChange} />
    </Labelled>
    <Labelled label="Refresh">
      <CacheAwareRefreshButton onRefresh={onRefresh} isLoading={isLoading} cacheInfo={cacheInfo} />
    </Labelled>
    <Labelled label="Export">
      <ExportCsv
        columns={tableProps.columns}
        data={tableProps.data}
        exportFileName="charge-points"
        getFieldValue={tableProps.getFieldValue}
        icons={tableProps.icons}
      />
    </Labelled>
    <ApplyUpdateAction chargePoints={allChargePoints} count={selectedCount} onFlexUpgrade={onFlexUpgrade} />
  </Box>
);
